import React, { useEffect, useRef, useState } from 'react';
import { H1 } from '@amzn/stencil-react-components/text';
import { Col, View } from '@amzn/stencil-react-components/layout';
import { Flex } from '@amzn/stencil-react-components/layout';
import { Button } from '@amzn/stencil-react-components/button';
import { Spacer } from '@amzn/stencil-react-components/layout';
import { Input } from '@amzn/stencil-react-components/input';
import { Select } from '@amzn/stencil-react-components/select';
import { Label, Text } from '@amzn/stencil-react-components/text';
import { PopoverPosition } from '@amzn/stencil-react-components/popover';
import { PopoverController } from '@amzn/stencil-react-components/popover';
import ButtonLink from './common/ButtonLink';
import SortableTable from './atoms/SortableTable';

import '../styles/CreatedSegments.css';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSegments } from '../actions/nudgeActions';
import segmentListFormatter from '../util/segmentCreation/segmentListFormatter';
import {
  deleteSegment,
  downloadSegment,
  setEditOperation,
  updateSegment,
} from '../actions/createSegmentActions';
import { useHistory } from 'react-router-dom';
import { CREATE_SEGMENT_PAGE, SCHEDULING_LANDING_PAGE } from '../constants/routesConstant';
import { updateCampaignForm } from '../actions/createScheduleAction';
import { CampaignFormSection } from '../enums/schedulingConstant';
import { FormattedMessage } from 'react-intl';
import { ResponseStatus } from '../enums/responseStatus';
import { getStatusIcon } from '../util/statusIndicatorUtil';
import { CREATED_SEGMENT_FILTER_OPTION } from '../constants/createSegment';
import isEmpty from 'lodash/isEmpty';
import { useUserInfo } from '../hooks/useUserInfo';
import useRuleFilter from '../hooks/useRuleFilter';
import { segmentListSelector } from '../selectors/selectors';
import { getSegmentById } from '../util/commonUtils';
import cloneDeep from 'lodash/cloneDeep';

import { IconSearchSmall } from '@amzn/stencil-react-icons';
import { IconRefreshSmall } from '@amzn/stencil-react-icons';

export const CreateSegmentLink = () => (
  <ButtonLink to={CREATE_SEGMENT_PAGE}>Create new segment</ButtonLink>
);

const DetailsActionButton = data => {
  const history = useHistory();
  const popoverTarget = useRef();
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const { populateSegmentData } = useRuleFilter();
  const segmentList = useSelector(segmentListSelector);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefresing] = useState(false);
  const close = () => setPopoverOpen(false);
  const {
    data: { segmentId = '', segmentName = '', status },
  } = data;
  const index = segmentList?.findIndex(
    segment => segment.segmentIdentifier.segmentId === segmentId,
  );
  const token = userInfo.idToken;

  const onDeleteSegment = async () => {
    setIsDeleting(true);
    await dispatch(deleteSegment({ segmentId }, token));
    await dispatch(getAllSegments(userInfo.selectedAccessGroupName, token));
    setIsDeleting(false);
    close();
  };

  const onDownloadSegment = async () => {
    setIsDownloading(true);
    await dispatch(downloadSegment({ segmentId }, token));
    setIsDownloading(false);
  };

  const onCreateCampaign = async () => {
    await dispatch(updateCampaignForm(CampaignFormSection.SEGMENT, { segmentId, segmentName }));
    history.push(SCHEDULING_LANDING_PAGE);
    close();
  };

  const onSegmentRefresh = async () => {
    setIsRefresing(true);
    const payload = {
      segment: getSegmentById(segmentId, segmentList),
      userName: userInfo.user,
      userGroupName: userInfo.selectedAccessGroupName,
    };
    await dispatch(updateSegment(payload, token));
    setIsRefresing(false);
  };

  const onEditSegment = async () => {
    await dispatch(setEditOperation(true));
    const segment = getSegmentById(segmentId, segmentList);
    populateSegmentData(cloneDeep(segment));
    history.push(CREATE_SEGMENT_PAGE);
  };

  return (
    <div>
      <Button
        ref={popoverTarget}
        hasIcon={false}
        iconOnly={false}
        onClick={() => {
          setPopoverOpen(!isPopoverOpen);
        }}
        data-testid="action-btn"
        small={false}
        tertiary={true}
      >
        ...
      </Button>

      <PopoverController
        data-testid="action-menu"
        target={popoverTarget}
        isOpen={isPopoverOpen}
        close={close}
        position={PopoverPosition.BottomCenter}
        shouldCloseOnFocusLeave={true}
        padding={30}
      >
        <Col alignItems="flex-start">
          {!isEmpty(segmentList[index]?.ruleDetails) ? (
            <Button
              data-testid="refresh-segment-button"
              className="segment-action-button"
              tertiary
              onClick={onSegmentRefresh}
              disabled={isRefreshing}
            >
              <Text>
                <FormattedMessage
                  id="app.createdSegments.label.refreshSegment"
                  defaultMessage="Refresh"
                />
              </Text>
              <Spacer width={7} />
              {isRefreshing ? <Spinner size={SpinnerSize.Small} /> : null}
            </Button>
          ) : null}
          {!isEmpty(segmentList[index]?.ruleDetails) ? (
            <Button
              data-testid="edit-segment-button"
              className="segment-action-button"
              tertiary
              onClick={onEditSegment}
            >
              <Text>
                <FormattedMessage
                  id="app.createdSegments.label.editSegment"
                  defaultMessage="Edit"
                />
              </Text>
              <Spacer width={7} />
              {/* {isRefreshing ? <Spinner size={SpinnerSize.Small} /> : null} */}
            </Button>
          ) : null}
          <Button
            data-testid="delete-campaign-btn"
            className="segment-action-button"
            tertiary
            onClick={onDeleteSegment}
            disabled={isDeleting}
          >
            <Text>
              <FormattedMessage id="app.createSegment.label.delete" defaultMessage="Delete" />
            </Text>
            <Spacer width={7} />
            {isDeleting ? <Spinner size={SpinnerSize.Small} /> : null}
          </Button>

          <Button
            data-testid="download-segment-btn"
            disabled={status?.toLowerCase() === ResponseStatus.ERROR}
            className="segment-action-button"
            tertiary
            onClick={onDownloadSegment}
          >
            <Text>
              <FormattedMessage id="app.createdSegment.label.download" defaultMessage="Download" />
            </Text>
            <Spacer width={7} />
            {isDownloading ? <Spinner size={SpinnerSize.Small} /> : null}
          </Button>

          <Button
            data-testid="create-campaign-btn"
            className="segment-action-button"
            disabled={status?.toLowerCase() === ResponseStatus.ERROR}
            tertiary
            onClick={onCreateCampaign}
          >
            <FormattedMessage
              id="app.createSegment.label.createCampaign"
              defaultMessage="Create Campaign"
            />
          </Button>
        </Col>
      </PopoverController>
    </div>
  );
};

export const StatusIndicator = data => {
  const {
    data: { status },
  } = data;
  return getStatusIcon(status);
};

const CreatedSegments = props => {
  const [filterBy, setFilterBy] = useState('Segment Name');
  const dispatch = useDispatch();
  const segmentList = useSelector(state => state.nudge.segmentList);
  const userInfo = useSelector(state => state.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  const filterIdMapping = {
    'Segment Name': 'segmentName',
  };
  const [filteredData, setFilteredData] = useState([]);
  const token = userInfo.idToken ?? JSON.parse(storage.getItem('idToken'));

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(getAllSegments(userInfo.selectedAccessGroupName, token));
      setIsLoading(false);
    })();
  }, []);

  let transformSegments = segmentListFormatter(segmentList);
  let data = [];
  useEffect(
    () => {
      transformSegments = segmentListFormatter(segmentList);
      setFilteredData(transformSegments || []);
      data = transformSegments || [];
    },
    [segmentList],
  );
  const columns = [
    { header: 'Segment name', accessor: 'segmentName', sortID: 'segmentName' },
    { header: 'Segment Description', accessor: 'segmentDescription', sortID: 'segmentId' },
    { header: 'Last modified', accessor: 'lastUpdatedDate', sortID: 'lastUpdatedDate' },
    { header: 'Modified by', accessor: 'lastUpdatedBy', sortID: 'lastUpdatedBy' },
    { header: 'Status', cellComponent: StatusIndicator },
    {
      header: 'Action',
      cellComponent: DetailsActionButton,
    },
  ];

  const onSearch = e => {
    const input = e.target.value;
    const filterId = filterIdMapping[filterBy];
    const searchedData = transformSegments.filter(segment =>
      segment[filterId].toLowerCase().includes(input.toLowerCase()),
    );
    setFilteredData(searchedData);
  };

  const onRefresh = async () => {
    setIsLoading(true);
    await dispatch(getAllSegments(userInfo.selectedAccessGroupName, token));
    setIsLoading(false);
  };

  return (
    <Col gridGap="S300">
      <View>
        <H1>
          <FormattedMessage id="app.createdSegments.label.segments" defaultMessage="Segments" />
        </H1>
      </View>
      <View flex="1" flexDirection="row" alignItems={'flex-end'} alignSelf={'flex-end'}>
        <CreateSegmentLink />
      </View>
      <View>
        <Text>
          <FormattedMessage
            id="app.createdSegments.label.createdSegments"
            defaultMessage="Created Segments"
          />
          ({segmentList?.length || 0})
        </Text>
      </View>
      <Flex flexDirection="row" gridGap="S100">
        <Col width="30%">
          <Label htmlFor="filter-segment-select" id="filter-segment-select-label">
            <FormattedMessage
              id="app.createdSegments.label.filterSegmentsBy"
              defaultMessage="Filter segments by"
            />
          </Label>
          <Flex flexDirection="row" gridGap="S100">
            <Select
              id="filter-segment-select"
              labelId="filter-segment-select-label"
              onChange={setFilterBy}
              options={CREATED_SEGMENT_FILTER_OPTION}
              value={filterBy}
            />
            <Spacer width="5px" />
            <Col
              display="flex"
              justifyContent="center"
              title="Refresh list"
              data-testid="refresh-icon"
              onClick={onRefresh}
              style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem' }}
            >
              <IconRefreshSmall aria-hidden="true" color="primary" style={{ cursor: 'pointer' }} />
            </Col>
          </Flex>
        </Col>
        <Col width="30%">&nbsp;</Col>
        <Spacer width="5%" />
        <Col width="30%">
          <Label htmlFor="some-search-field" id="some-search-label">
            <FormattedMessage
              id="app.createdSegments.label.searchSegment"
              defaultMessage="Segment search"
            />
          </Label>
          <Input
            id="segmentSearch"
            data-testid="segment-search"
            iconLeft={IconSearchSmall}
            onChange={e => {
              onSearch(e);
            }}
          />
        </Col>
      </Flex>
      <Col gridGap={0}>
        {isLoading && (
          <Col gridGap={'S100'} padding={10} alignItems="center">
            <View alignItems="center">
              <Spinner size={SpinnerSize.Medium} />
            </View>
          </Col>
        )}
        {!isLoading &&
          !isEmpty(filteredData) && (
            <SortableTable
              columns={columns}
              defaultActiveSortId={'lastUpdatedDate'}
              data={filteredData}
            />
          )}
        {!isLoading &&
          isEmpty(filteredData) && (
            <Col gridGap={'S100'} alignItems="center">
              <View alignItems="center">
                <Text>
                  <FormattedMessage
                    id="app.createdSegments.label.noSegmentsAvailable"
                    defaultMessage="No segments available."
                  />
                </Text>
              </View>
            </Col>
          )}
      </Col>
    </Col>
  );
};

export default CreatedSegments;
